'use client';

import clsx from 'clsx';
import { useState } from 'react';
import { TooltipArrow, TooltipContent, TooltipRoot, TooltipTrigger } from '@/components/Tooltip/BaseTooltip';
import { useDeviceType } from '@/hooks/useDeviceType';
import './Tooltip.scss';

export type TooltipProps = {
	arrowClassName?: string;
	children: React.ReactNode;
	className?: string;
	tooltipTriggerAsChild?: boolean;
	tooltipTriggerChild?: React.ReactNode;
	align?: 'center' | 'end' | 'start';
	side?: 'top' | 'right' | 'bottom' | 'left';
};

export function Tooltip({
	arrowClassName,
	children,
	className,
	tooltipTriggerAsChild,
	tooltipTriggerChild,
	align = 'start',
	side = 'right'
}: Readonly<TooltipProps>) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { isTouchDevice, isNarrowScreen } = useDeviceType();

	const resolvedSide = isNarrowScreen && (side === 'left' || side === 'right') ? 'bottom' : side;

	const handleOpenChange = (open: boolean) => {
		// On touch devices, block Radix from opening (prevents hover/focus race
		// conditions with the click handler) but allow it to close (so the
		// TooltipProvider can dismiss other tooltips when a new one opens).
		if (isTouchDevice && open) return;
		setIsOpen(open);
	};

	const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isTouchDevice) {
			event.preventDefault(); //required in case used within an input label
			setIsOpen((prev) => !prev);
		}
	};

	return (
		<TooltipRoot open={isOpen} onOpenChange={handleOpenChange} delayDuration={0}>
			<TooltipTrigger asChild={tooltipTriggerAsChild} onClick={handleToggle}>
				{tooltipTriggerChild}
			</TooltipTrigger>
			<TooltipContent
				className={clsx('[ tooltip__content ] [ text-caption-1 color-text-white ]', className)}
				arrowPadding={8}
				collisionPadding={16}
				side={resolvedSide}
				align={align}
				alignOffset={-16}
			>
				{children}
				<TooltipArrow asChild>
					<span className={clsx('tooltip__arrow', arrowClassName)} />
				</TooltipArrow>
			</TooltipContent>
		</TooltipRoot>
	);
}
